import { useState } from "react";
import { toast } from "sonner";
import { Printer, FileText } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const PRESETS = [["CA", "Québec", "Montréal"], ["CA", "Québec", "Laval"], ["CA", "Québec", "Québec"], ["CA", "Québec", ""], ["CA", "", ""], ["FR", "Île-de-France", "Paris"], ["FR", "", ""]];
const COUNTRY = { CA: "Canada", FR: "France" };
const M = [["users", "Joueurs ayant déclaré cette zone"], ["active_7", "Joueurs actifs (7 jours)"], ["active_30", "Joueurs actifs (30 jours)"], ["predictors_30", "Joueurs ayant pronostiqué (30 jours)"],
  ["new_30", "Nouveaux joueurs (30 jours)"], ["location_consent", "Audience publicitaire locale (accord offres régionales)"], ["marketing_consent", "Joueurs acceptant les offres par courriel"]];
const val = (v) => (v == null ? "< 5" : Number(v).toLocaleString("fr-CA"));

function Sheet({ d }) {
  const z = d.zone;
  const name = z.city || z.region || COUNTRY[z.country] || z.country;
  return (
    <article className="city-sheet print-area rounded-2xl bg-white p-8 text-[#0B1020]" data-testid="city-sheet">
      <header className="flex items-end justify-between border-b-4 border-[#B7F52A] pb-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Ma Petite Ligue · Fiche commerciale</p>
          <h2 className="font-display text-4xl font-extrabold uppercase" data-testid="city-sheet-title">{name}</h2>
          <p className="text-sm text-slate-600">{[...new Set([z.city, z.region, COUNTRY[z.country]].filter(Boolean))].join(" · ")}</p>
        </div>
        <p className="text-right text-[11px] text-slate-500">Générée le {new Date(d.generated_at).toLocaleDateString("fr-CA")}<br />Données réelles, période : 30 derniers jours</p>
      </header>
      <section className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {M.map(([k, l]) => (
          <div key={k} className="rounded-xl border border-slate-200 p-3" data-testid={`city-sheet-${k}`}>
            <p className="font-display text-3xl font-extrabold">{val(d.metrics[k])}</p>
            <p className="text-[11px] leading-tight text-slate-600">{l}</p>
          </div>
        ))}
        <div className="rounded-xl border border-slate-200 p-3" data-testid="city-sheet-leagues">
          <p className="font-display text-3xl font-extrabold">{val(d.leagues_with_players.private)}</p>
          <p className="text-[11px] leading-tight text-slate-600">Ligues privées avec des joueurs de la zone</p>
        </div>
      </section>
      <section className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <h3 className="text-sm font-bold uppercase">Publicité dans la zone (30 jours)</h3>
          <p className="mt-1 text-sm">Affichages visibles : <b>{val(d.ads_30d.viewable)}</b> · Joueurs touchés : <b>{val(d.ads_30d.reach)}</b> · Clics : <b>{val(d.ads_30d.clicks)}</b></p>
          <h3 className="mt-4 text-sm font-bold uppercase">Emplacements proposés</h3>
          <ul className="mt-1 list-disc pl-5 text-sm">{d.placements.filter((p) => p.active).map((p) => <li key={p.label}>{p.label}{p.where && <span className="text-slate-500"> — {p.where}</span>}</li>)}</ul>
        </div>
        <div>
          <h3 className="text-sm font-bold uppercase">Pourquoi Ma Petite Ligue</h3>
          <ul className="mt-1 list-disc pl-5 text-sm">
            <li>Jeu social de pronostics NBA entre amis et collègues, gratuit, sans pari ni argent.</li>
            <li>Offres toujours identifiées comme partenaires, dans des espaces dédiés.</li>
            <li>Ciblage par ville déclarée volontairement, sans GPS.</li>
            <li>Rapports agrégés : aucune donnée personnelle de joueur n'est transmise.</li>
          </ul>
        </div>
      </section>
      <footer className="mt-6 border-t border-slate-200 pt-3 text-[10px] leading-snug text-slate-500">
        <p><b>Méthode :</b> {d.method}</p>
        <p><b>Limites :</b> {d.limits}</p>
        <p className="mt-1">Contact : hello@mapetiteligue.com · mapetiteligue.com</p>
      </footer>
    </article>
  );
}

export default function CitySheet({ onExpired, onPropose }) {
  const [z, setZ] = useState({ country: "CA", region: "Québec", city: "Montréal" });
  const [d, setD] = useState(null);
  const load = async (zone = z) => {
    try {
      const q = new URLSearchParams({ country: zone.country, region: zone.region, city: zone.city });
      setD((await api.get(`/superadmin/regie/city-sheet?${q}`)).data);
    } catch (e) { if (e.response?.status === 428) onExpired?.(); else toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-3" data-testid="city-sheet-tool">
      <div className="mpl-card space-y-2 p-4 text-xs no-print">
        <div className="flex flex-wrap gap-1.5">{PRESETS.map(([c, r, v]) => (
          <button key={`${c}${r}${v}`} onClick={() => { const n = { country: c, region: r, city: v }; setZ(n); load(n); }} className="rounded-full bg-white/5 px-3 py-1 text-[11px] text-slate-200 hover:bg-white/10" data-testid={`city-preset-${v || r || c}`}>{v || r || COUNTRY[c]}</button>))}
        </div>
        <div className="grid gap-2 sm:grid-cols-4">
          <select className="mpl-input" value={z.country} onChange={(e) => setZ({ ...z, country: e.target.value })} data-testid="city-sheet-country"><option value="CA">Canada</option><option value="FR">France</option></select>
          <input className="mpl-input" placeholder="Région (facultatif)" value={z.region} onChange={(e) => setZ({ ...z, region: e.target.value })} data-testid="city-sheet-region" />
          <input className="mpl-input" placeholder="Ville (facultatif)" value={z.city} onChange={(e) => setZ({ ...z, city: e.target.value })} data-testid="city-sheet-city" />
          <button onClick={() => load()} className="mpl-btn-primary" data-testid="city-sheet-generate"><FileText size={13} /> Générer la fiche</button>
        </div>
      </div>
      {d && (<>
        <div className="flex flex-wrap gap-2 no-print">
          <button onClick={() => window.print()} className="mpl-btn-ghost text-xs" data-testid="city-sheet-print"><Printer size={13} /> Imprimer ou enregistrer en PDF</button>
          {onPropose && <button onClick={() => onPropose({ country: d.zone.country, region: d.zone.region || "", city: d.zone.city || "" })} className="mpl-btn-primary text-xs" data-testid="city-sheet-propose"><FileText size={13} /> Créer une proposition pour cette zone</button>}
        </div>
        <Sheet d={d} />
      </>)}
    </section>
  );
}
